import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  phone?: string;
  is_active: boolean;
  is_superuser: boolean;
  notification_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface AdminUserCreate {
  username: string;
  email: string;
  phone?: string;
  password: string;
  is_active: boolean;
  is_superuser: boolean;
  notification_enabled: boolean;
}

interface AdminUserUpdate {
  username?: string;
  email?: string;
  phone?: string;
  password?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  notification_enabled?: boolean;
}

export const AdminUsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [formData, setFormData] = useState<AdminUserCreate>({
    username: '',
    email: '',
    phone: '',
    password: '',
    is_active: true,
    is_superuser: false,
    notification_enabled: true,
  });

  // 휴대폰 번호 포맷팅 함수
  const formatPhoneNumber = (value: string) => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '');

    // 최대 11자리까지만
    const truncated = numbers.slice(0, 11);

    // 포맷팅: 010-1234-5678
    if (truncated.length <= 3) {
      return truncated;
    } else if (truncated.length <= 7) {
      return `${truncated.slice(0, 3)}-${truncated.slice(3)}`;
    } else {
      return `${truncated.slice(0, 3)}-${truncated.slice(3, 7)}-${truncated.slice(7)}`;
    }
  };

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await apiClient.get<AdminUser[]>('/admin-users/');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: AdminUserCreate) => {
      const response = await apiClient.post('/admin-users/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setIsCreateModalOpen(false);
      resetForm();
      alert('관리자가 생성되었습니다.');
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || '생성 중 오류가 발생했습니다.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AdminUserUpdate }) => {
      const response = await apiClient.put(`/admin-users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditingUser(null);
      resetForm();
      alert('관리자 정보가 수정되었습니다.');
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || '수정 중 오류가 발생했습니다.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/admin-users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      alert('관리자가 삭제되었습니다.');
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || '삭제 중 오류가 발생했습니다.');
    },
  });

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      phone: '',
      password: '',
      is_active: true,
      is_superuser: false,
      notification_enabled: true,
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password) {
      alert('필수 항목을 입력해주세요.');
      return;
    }
    createMutation.mutate(formData);
  };

  const handleEdit = (user: AdminUser) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      phone: user.phone || '',
      password: '',
      is_active: user.is_active,
      is_superuser: user.is_superuser,
      notification_enabled: user.notification_enabled,
    });
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    const updateData: AdminUserUpdate = {
      username: formData.username !== editingUser.username ? formData.username : undefined,
      email: formData.email !== editingUser.email ? formData.email : undefined,
      phone: formData.phone !== editingUser.phone ? formData.phone : undefined,
      password: formData.password || undefined,
      is_active: formData.is_active !== editingUser.is_active ? formData.is_active : undefined,
      is_superuser: formData.is_superuser !== editingUser.is_superuser ? formData.is_superuser : undefined,
      notification_enabled: formData.notification_enabled !== editingUser.notification_enabled ? formData.notification_enabled : undefined,
    };

    updateMutation.mutate({ id: editingUser.id, data: updateData });
  };

  const handleDelete = (user: AdminUser) => {
    if (window.confirm(`${user.username}을(를) 삭제하시겠습니까?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  return (
    <Layout>
      <div className="px-2 py-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">관리자 관리</h1>
            <p className="mt-1 text-sm text-gray-600">시스템 관리자 계정을 관리합니다.</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsCreateModalOpen(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
          >
            관리자 추가
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              관리자 목록
              {users && users.length > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({users.length}명)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">데이터를 불러오는데 실패했습니다.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">사용자명</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이메일</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">휴대폰</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">활성화</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">슈퍼유저</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">알림수신</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">생성일</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">액션</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users && users.length > 0 ? (
                    users.slice((page - 1) * limit, page * limit).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.username}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.phone || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            user.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.is_superuser ? '예' : '아니오'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.notification_enabled ? '예' : '아니오'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => handleEdit(user)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title="수정"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(user)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title="삭제"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">
                        관리자가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {users && users.length > limit && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-600">
                      페이지 <span className="font-medium text-gray-900">{page}</span> / {Math.ceil(users.length / limit)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      이전
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(users.length / limit)}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      다음
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {(isCreateModalOpen || editingUser) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="bg-white border-b border-gray-200 px-5 py-4 rounded-t-lg">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingUser ? '관리자 수정' : '관리자 추가'}
                </h3>
              </div>

              <form onSubmit={editingUser ? handleUpdate : handleCreate} className="p-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">사용자명</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      required={!editingUser}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">이메일</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      required={!editingUser}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">휴대폰 번호</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setFormData({ ...formData, phone: formatted });
                      }}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder="010-1234-5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      비밀번호 {editingUser && '(변경시에만 입력)'}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      required={!editingUser}
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">활성화</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_superuser}
                      onChange={(e) => setFormData({ ...formData, is_superuser: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">슈퍼유저</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notification_enabled}
                      onChange={(e) => setFormData({ ...formData, notification_enabled: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">알림 수신</label>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setEditingUser(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    {editingUser ? '수정' : '생성'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
